import React, { useMemo, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, FilterIcon, StarIcon } from 'lucide-react';
import { useVideos, Video } from './VideosContext';
interface VideosListProps {
  onEdit: (video: Video) => void;
  onDelete: (video: Video) => void;
  onAdd: () => void;
}
export function VideosList({
  onEdit,
  onDelete,
  onAdd
}: VideosListProps) {
  const {
    videos
  } = useVideos();
  const [pathFilter, setPathFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const filteredVideos = useMemo(() => {
    return videos.filter(video => {
      if (pathFilter === 'all') return true;
      return video.path === pathFilter;
    }).filter(video => {
      if (!searchTerm) return true;
      return video.title.toLowerCase().includes(searchTerm.toLowerCase()) || video.description.toLowerCase().includes(searchTerm.toLowerCase()) || video.author && video.author.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [videos, pathFilter, searchTerm]);
  const pathOptions = [{
    value: 'all',
    label: 'جميع المسارات'
  }, {
    value: 'husband',
    label: 'مسار الزوج'
  }, {
    value: 'wife',
    label: 'مسار الزوجة'
  }, {
    value: 'engaged',
    label: 'المقبلين على الزواج'
  }, {
    value: 'both',
    label: 'الزوجين معاً'
  }, {
    value: 'general',
    label: 'معرفة عامة'
  }];
  const getPathLabel = (path: string) => {
    const option = pathOptions.find(opt => opt.value === path);
    return option ? option.label : path;
  };
  return <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-908">
      <div className="p-4 bg-sky-50 border-b border-sky-100 flex justify-between items-center" data-id="element-909">
        <h2 className="font-bold text-lg text-sky-900" data-id="element-910">
          إدارة الفيديوهات التوعوية
        </h2>
        <button onClick={onAdd} className="px-3 py-1.5 bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors flex items-center text-sm" data-id="element-911">
          <PlusIcon className="h-4 w-4 ml-1.5" data-id="element-912" />
          <span data-id="element-913">إضافة فيديو جديد</span>
        </button>
      </div>
      <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row gap-4" data-id="element-914">
        <div className="w-full md:w-1/3" data-id="element-915">
          <div className="flex items-center" data-id="element-916">
            <FilterIcon className="h-4 w-4 ml-2 text-slate-500" data-id="element-917" />
            <label className="text-sm text-slate-600" data-id="element-918">تصفية حسب المسار:</label>
          </div>
          <select value={pathFilter} onChange={e => setPathFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 py-2 px-3 text-sm focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-919">
            {pathOptions.map(option => <option key={option.value} value={option.value} data-id="element-920">
                {option.label}
              </option>)}
          </select>
        </div>
        <div className="w-full md:w-2/3" data-id="element-921">
          <div className="flex items-center" data-id="element-922">
            <EyeIcon className="h-4 w-4 ml-2 text-slate-500" data-id="element-923" />
            <label className="text-sm text-slate-600" data-id="element-924">بحث في الفيديوهات:</label>
          </div>
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="ابحث بالعنوان أو الوصف أو اسم المؤلف..." className="mt-1 w-full rounded-md border border-slate-200 py-2 px-3 text-sm focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-925" />
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-926">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-927">
          <thead className="bg-gray-50" data-id="element-928">
            <tr data-id="element-929">
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-930">
                الصورة
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-931">
                العنوان
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-932">
                المسار
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-933">
                مميز
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-934">
                تاريخ الإضافة
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-935">
                الإجراءات
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-936">
            {filteredVideos.length === 0 ? <tr data-id="element-937">
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-938">
                  لا توجد فيديوهات متاحة
                </td>
              </tr> : filteredVideos.map(video => <tr key={video.id} className="hover:bg-gray-50" data-id="element-939">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-940">
                    <img src={`https://img.youtube.com/vi/${video.youtubeId}/default.jpg`} alt={video.title} className="h-12 w-20 object-cover rounded" data-id="element-941" />
                  </td>
                  <td className="px-6 py-4" data-id="element-942">
                    <div className="text-sm font-medium text-gray-900" data-id="element-943">
                      {video.title}
                    </div>
                    {video.author && <div className="text-xs text-gray-500" data-id="element-944">
                        {video.author}
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-945">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-946">
                      {getPathLabel(video.path)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-947">
                    {video.isFeatured ? <StarIcon className="h-5 w-5 text-amber-500 inline-block" data-id="element-948" /> : <span className="text-gray-400" data-id="element-949">-</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-950">
                    {new Date(video.createdAt).toLocaleDateString('ar-SA')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium" data-id="element-951">
                    <div className="flex space-x-2 justify-end" data-id="element-952">
                      <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900 p-1" title="عرض الفيديو" data-id="element-953">
                        <EyeIcon className="h-5 w-5" data-id="element-954" />
                      </a>
                      <button onClick={() => onEdit(video)} className="text-amber-600 hover:text-amber-900 p-1" title="تعديل" data-id="element-955">
                        <PencilIcon className="h-5 w-5" data-id="element-956" />
                      </button>
                      <button onClick={() => onDelete(video)} className="text-rose-600 hover:text-rose-900 p-1" title="حذف" data-id="element-957">
                        <TrashIcon className="h-5 w-5" data-id="element-958" />
                      </button>
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-gray-100 bg-gray-50" data-id="element-959">
        <div className="text-sm text-gray-500" data-id="element-960">
          إجمالي الفيديوهات: {filteredVideos.length} من {videos.length}
        </div>
      </div>
    </div>;
}